import {
  fetchBaseQuery,
  retry,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import type { QueryReturnValue } from '@reduxjs/toolkit/query';
import * as Sentry from '@sentry/react';
import { maskPayLinkString } from '../lib/analytics/maskPayLinkTokens';
import { currentApp, currentUserId, markReported } from '../lib/errorReporting';
import {
  answered,
  networkFailureObserved,
  PROBE_MAX_SECONDS,
  probeClaimed,
  serviceUnavailable,
  type ServiceStateReason,
} from '../Redux/Slice/ServiceStateSlice';

export const getCsrfToken = (): string | null => {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

export const applyApiHeaders = (
  headers: Headers,
  token: string | null
): Headers => {
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const printerId = localStorage.getItem('selectedPrinter');
  if (printerId) {
    headers.set('X-Printer-ID', printerId);
  }
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    headers.set('X-CSRFToken', csrfToken);
  }
  return headers;
};

const createBaseQuery = (token: string | null) => {
  return fetchBaseQuery({
    baseUrl: '/api',
    prepareHeaders: headers => applyApiHeaders(headers, token),
  });
};

const logErrorLocally = (errorDetails: any) => {
  console.error('API Error:', errorDetails);
};

const requestUrl = (args: any): string =>
  typeof args === 'string' ? args : (args?.url ?? '');

const requestMethod = (args: any): string =>
  (typeof args === 'string' ? 'GET' : (args?.method ?? 'GET')).toUpperCase();

// Collapses the identifiers RTK Query bakes into request URLs -- database ids,
// student numbers (`0676-2024`), uuids -- so every parent hitting the same
// broken endpoint lands in one Sentry issue instead of one issue per child.
export const normalizeApiPath = (url: string): string => {
  const rawPath = url.split('?')[0];
  // Only the public pay-link routes carry a bearer token in the path.
  const path = /^\/pay(?:-invoice)?\//.test(rawPath)
    ? maskPayLinkString(rawPath)
    : rawPath;
  return path
    .split('/')
    .map(segment => {
      if (/^\d+$/.test(segment)) return ':id';
      if (/^\d{3,}-\d{4}$/.test(segment)) return ':studentNumber';
      if (
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          segment
        )
      ) {
        return ':uuid';
      }
      return segment;
    })
    .join('/');
};

const CODED_SERVICE_REASONS = ['service_unavailable', 'maintenance'] as const;

// Only the API's own outage codes count; an uncoded 503 is the BFF timing out
// on one slow request and says nothing about the service as a whole.
const codedServiceReason = (
  error: any
): Exclude<ServiceStateReason, 'network'> | null => {
  if (error?.status !== 503) return null;
  const code = error?.data?.code;
  return CODED_SERVICE_REASONS.find(reason => reason === code) ?? null;
};

const parseRetryAfterSeconds = (meta: any): number | null => {
  const raw = meta?.response?.headers?.get?.('Retry-After');
  const seconds = Number(raw);
  return raw && Number.isFinite(seconds) && seconds >= 0
    ? Math.round(seconds)
    : null;
};

// A response below 500 (bar 429, which the BFF's rate limiter can answer on its
// own) proves the API itself is serving again.
const apiAnswered = (error: any) =>
  !error ||
  (typeof error.status === 'number' &&
    error.status < 500 &&
    error.status !== 429);

// Stores built without the slice (isolated API tests) simply have no state.
const readServiceState = (api: any) => api.getState().serviceState ?? null;

const observeServiceState = (
  api: any,
  args: any,
  result: any,
  startedAt: number
) => {
  const { error } = result;
  const reason = codedServiceReason(error);
  if (reason) {
    api.dispatch(
      serviceUnavailable({
        reason,
        retryAfterSeconds: parseRetryAfterSeconds(result.meta),
        at: Date.now(),
      })
    );
  } else if (error?.status === 'FETCH_ERROR') {
    // Offline is the user's connection, not an outage on our side.
    if (navigator.onLine !== false) {
      api.dispatch(
        networkFailureObserved({
          path: normalizeApiPath(requestUrl(args)),
          at: Date.now(),
        })
      );
    }
  } else if (apiAnswered(error)) {
    const current = readServiceState(api);
    if (
      current &&
      (current.status === 'unavailable' ||
        current.recentNetworkFailures.length > 0)
    ) {
      api.dispatch(answered({ startedAt }));
    }
  }
};

// While unavailable, queries (including polls and focus refetches) are held
// back without touching the network. One per interval is let through as the
// probe that notices recovery. Saves always go through: they are the person's
// own action and deserve a real answer.
const holdBackQuery = (
  api: any,
  extraOptions?: any
): QueryReturnValue<
  unknown,
  FetchBaseQueryError,
  FetchBaseQueryMeta
> | null => {
  if (api.type !== 'query') return null;
  // Checkout-status reads answer "did my money move?" for someone waiting on
  // the screen. Held, they show a false "no answer yet" after recovery while
  // the screen offers a second payment. Still never retried while unavailable
  // (`retryCondition`), and they do not take the probe slot.
  if (extraOptions?.bypassOutageHold) return null;
  const current = readServiceState(api);
  if (current?.status !== 'unavailable') return null;
  const now = Date.now();
  // A clock stepped backwards must not stop the probe going out. Clearing the
  // state on its answer is a separate gap (`answered` compares against `since`).
  const clockStepped =
    current.nextProbeAt !== null &&
    current.nextProbeAt - now > PROBE_MAX_SECONDS * 1000;
  if (
    current.nextProbeAt !== null &&
    now < current.nextProbeAt &&
    !clockStepped
  ) {
    return {
      error: {
        status: 503,
        data: {
          errors: ['Service temporarily unavailable.'],
          code:
            current.reason === 'maintenance'
              ? 'maintenance'
              : 'service_unavailable',
        },
      },
    };
  }
  api.dispatch(probeClaimed({ at: now }));
  return null;
};

// Backend error text is free-form -- it can name a student or an amount -- so
// it never leaves the tenant boundary. Only which key carried it is reported.
type ReasonKey = 'error' | 'string' | 'errors' | 'detail' | 'message' | 'none';

const errorReasonKey = (error: any): ReasonKey => {
  if (typeof error?.error === 'string') return 'error';
  const data = error?.data;
  if (typeof data === 'string') return 'string';
  if (data?.errors?.[0] != null) return 'errors';
  if (data?.detail != null) return 'detail';
  if (data?.message != null) return 'message';
  return 'none';
};

/**
 * Reports a failed API request to Sentry.
 *
 * RTK Query resolves failures into `result.error` rather than throwing, so
 * nothing here ever reaches Sentry's global handlers -- the user just gets the
 * "Oops! Something went wrong" screen and we never hear about it. This closes
 * that blind spot.
 *
 * 401s are skipped: `ErrorMessage` turns those into a login redirect, which is
 * expected behaviour rather than a fault. Called only on the request's final
 * outcome (see `baseQuery` below) -- not on every retried attempt, so a
 * request that fails twice and then succeeds never reports a false failure.
 */
export const reportApiError = (error: any, args: any) => {
  if (process.env.NODE_ENV !== 'production') return;

  const status = error?.status;
  if (status === 401) return;
  const serviceReason = codedServiceReason(error);
  // Maintenance is refused before anything runs, and a failed read has nothing
  // to reconcile. A failed write may have landed before the connection dropped,
  // and this event is the per-request record of which user's write is unknown.
  if (serviceReason === 'maintenance') return;
  if (serviceReason && requestMethod(args) === 'GET') return;

  try {
    // `.unwrap()` re-throws this exact object, so mark it before reporting to
    // stop a downstream `catch` from filing the same failure a second time.
    markReported(error);

    const method = requestMethod(args);
    const path = normalizeApiPath(requestUrl(args));
    const isServerFault =
      typeof status !== 'number' || status >= 500 || status === 0;

    Sentry.captureException(new Error(`API ${status} ${method} /api${path}`), {
      level: isServerFault ? 'error' : 'warning',
      fingerprint: ['api-error', method, path, String(status)],
      user: { id: currentUserId() },
      tags: {
        'api.status': String(status),
        'api.method': method,
        'api.path': path,
        app: currentApp(),
      },
      extra: {
        // Normalized, like `path` above -- the raw URL can carry a real
        // student number/id, and this leaves the tenant boundary to Sentry.
        url: path,
        reasonKey: errorReasonKey(error),
      },
    });
  } catch {
    // Reporting must never break the flow that was already failing.
  }
};

const rawBaseQuery = async (args: any, api: any, extraOptions: any) => {
  const token = localStorage.getItem('token');
  const user = localStorage.getItem('user');

  const startedAt = Date.now();
  const baseQueryFunction = createBaseQuery(token);
  const result = await baseQueryFunction(args, api, extraOptions);
  observeServiceState(api, args, result, startedAt);

  if (result.error) {
    const errorDetails = {
      status: result.error.status,
      data: result.error.data,
      method: requestMethod(args),
      path: normalizeApiPath(requestUrl(args)),
      timestamp: new Date().toISOString(),
      user: user ?? 'Anonymous',
    };
    logErrorLocally(errorDetails);
  }

  return result;
};

// Deterministic client errors (4xx — locked sheet, over-max mark, permission
// denied, etc.) can never be fixed by retrying, so only retry on network
// failures/timeouts (no numeric status) or server errors (5xx).
const isRetryableError = (error: any) =>
  typeof error?.status !== 'number' || error.status >= 500;

const MAX_RETRIES = 3;

// Queries are safe to retry by default (idempotent reads). Mutations are NOT
// retried unless the endpoint explicitly opts in via `extraOptions: {
// enableMutationRetry: true }` — several mutations in this app (payments,
// staged-payment approval) create a new record on every call with no
// server-side idempotency guard, so blanket-retrying them risks a duplicate
// charge/record on a lost-response resend. Only opt in an endpoint whose
// server-side handler is confirmed safe to receive the same request twice.
const baseQueryWithRetry = retry(rawBaseQuery, {
  retryCondition: (error, _args, { attempt, baseQueryApi, extraOptions }) => {
    if (attempt > MAX_RETRIES) return false;
    if (codedServiceReason(error)) return false;
    if (readServiceState(baseQueryApi)?.status === 'unavailable') return false;
    const canRetryThisEndpoint =
      baseQueryApi.type === 'query' ||
      Boolean((extraOptions as any)?.enableMutationRetry);
    return canRetryThisEndpoint && isRetryableError(error);
  },
});

// Reports only the request's final outcome -- `baseQueryWithRetry` already
// absorbed every retryable failure internally, so by the time control
// reaches here `result.error` (if present) is the one the user actually saw.
const baseQuery = async (args: any, api: any, extraOptions: any) => {
  const held = holdBackQuery(api, extraOptions);
  if (held) return held;

  const result = await baseQueryWithRetry(args, api, extraOptions);

  if (result.error) {
    reportApiError(result.error, args);
  }

  return result;
};

export default baseQuery;
